import { z } from 'zod';
import { newOrganizationSchema, existingOrganizationSchema } from './organizationSchema';
import { modulesArraySchema } from './moduleSchema';

export const wizardStep1Schema = z.discriminatedUnion('organizationType', [
  z.object({
    organizationType: z.literal('New'),
    organization: newOrganizationSchema,
  }),
  z.object({
    organizationType: z.literal('Existing'),
    organization: existingOrganizationSchema,
  }),
]);

export type WizardStep1Data = z.infer<typeof wizardStep1Schema>;

// Step 2: Bridge lookup
export const wizardStep2Schema = z.object({
  bridgeEntityUuid: z
    .string()
    .min(1, 'Bridge entity UUID is required')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Invalid UUID format'
    ),
  bridgeValidationPassed: z.literal(true, {
    errorMap: () => ({ message: 'Bridge validation must pass before proceeding' }),
  }),
});

export type WizardStep2Data = z.infer<typeof wizardStep2Schema>;

// Step 3: Module selection (modules come from Bridge)
export const wizardStep3Schema = z.object({
  modules: modulesArraySchema,
});

export type WizardStep3Data = z.infer<typeof wizardStep3Schema>;

// Step 4: Platform validation (was step 5)
export const wizardStep4Schema = z.object({
  validationPassed: z.literal(true, {
    errorMap: () => ({ message: 'All validations must pass before proceeding' }),
  }),
});

export type WizardStep4Data = z.infer<typeof wizardStep4Schema>;

// Step 5: Confirmation (was step 6)
export const wizardStep5Schema = z.object({
  termsAccepted: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms to proceed' }),
  }),
});

export type WizardStep5Data = z.infer<typeof wizardStep5Schema>;

// Complete wizard data
export interface WizardCompleteData {
  organizationType: 'New' | 'Existing';
  // Bridge integration
  bridgeEntityUuid: string;
  bridgeEntityName?: string;
  // For new organizations
  organizationName?: string;
  streetAddress?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  billing?: string;
  sendInvite?: boolean;
  // For existing organizations
  amplifyOrganizationUuid?: string;
  // Modules
  modules: Array<{
    moduleKey: string;
    sourceId: string;
    bridgeProductId?: string;
    amplifyModuleId: number;
  }>;
}

export const WIZARD_STEPS = [
  { id: 1, title: 'Organization', description: 'Select organization type' },
  { id: 2, title: 'Bridge Lookup', description: 'Validate Bridge record' },
  { id: 3, title: 'Modules', description: 'Select modules' },
  { id: 4, title: 'Validation', description: 'Validate configuration' },
  { id: 5, title: 'Confirmation', description: 'Review and start migration' },
] as const;

export type WizardStepId = typeof WIZARD_STEPS[number]['id'];
